import { useMemo, useState } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { max, min } from 'd3-array'
import { scaleLinear } from '@visx/scale'
import { withParentSize } from '@visx/responsive'
import { Brush } from '@visx/brush'
import { Group } from '@visx/group'

import AreaChart from './LineAreaChart'

import { getValue } from './graphUtils'

// Brush style
const selectedBrushStyle = (colors) => ({
  fill: 'rgba(255,255,255,0.2)',
  stroke: colors.textPrimary,
  rx: '4'
})

function GraphBrush({
  parentWidth: width,
  height,
  layers,
  margin = { top: 0, bottom: 0, left: 0, right: 0 },
  setBrushRange,
  resetRange,
  brushRef,
  xScale,
}) {
  const { colors } = usePaletteTheme()
  const [ isHover, setIsHover ] = useState(false)
  const xMax = Math.max(width -  margin.left - margin.right, 0)
  const yMax = Math.max(height, 0)

  const onMouseLeave = () => setIsHover(false)
  const onMouseEnter = () => setIsHover(true)

  // update brushRange state when brush is changed (should trigger update to filteredLayers)
  const onBrushChange = (domain) => {
    if (!domain || !isHover) return
    const { x0, x1 } = domain
    const correctX1 = x1 > resetRange[1] ? resetRange[1] : x1
    setBrushRange([x0, correctX1])
  }
  
  const yScales = useMemo(() =>
    layers.map(layer => scaleLinear({
        range: [yMax, 0],
        domain: layer.yDomain || [min(layer.data, getValue), max(layer.data, getValue) || 0],
        nice: true,
      })),
    [yMax, layers],
  )
  const initialBrushPosition = useMemo(() => (
    resetRange.includes(null) 
      ? undefined
      : ({
          start: { x: xScale(resetRange[0]) },
          end: { x: xScale(resetRange[1]) },
        })
  ), [xScale, resetRange])

  const brushCharts = useMemo(() => (
    layers.map((layer, i) => (
      yScales.length === layers.length &&
        <AreaChart
          key={`brush-${layer.name}-${i}`}
          hideBottomAxis
          hideLeftAxis
          data={layers[i].data}
          width={xMax}
          yMax={yMax}
          xScale={xScale}
          yScale={yScales[i]}
          top={0}
          color={layer.color}
          area={layer.display === 'area'}
          line={layer.display !== 'none'}
        />
      ))
    )
  ,[layers, yScales, xScale, yMax, xMax])

  // clear brush when clicked
  const handleClearClick = () => {
    if (brushRef?.current) {
      setBrushRange([ null, new Date() ])
      brushRef.current.reset()
    }
  }
    
  return (
    <svg width={width} height={height} onMouseLeave={onMouseLeave} onMouseEnter={onMouseEnter}>
      <Group left={margin.left}>
        { brushCharts }
        <Brush
          xScale={xScale}
          yScale={yScales[0]}
          width={xMax}
          height={yMax}
          handleSize={8}
          innerRef={brushRef}
          resizeTriggerAreas={['left', 'right']}
          brushDirection="horizontal"
          initialBrushPosition={initialBrushPosition}
          onChange={onBrushChange}
          onClick={handleClearClick}
          selectedBoxStyle={selectedBrushStyle(colors)}
        />
      </Group>
    </svg>
  )
}

export default withParentSize(GraphBrush)